import { clutch, isNumberBetweenEquals } from "./number/number";

export const KeyCode = {
  leftArrow: 37,
  rightArrow: 39,
  upArrow: 38,
  downArrow: 40,
  space: 32,
  enter: 13,
  esc: 27,
  tab: 9,
  f3: 114,
  f: 70,
  l: 76,
  p: 80,
  s: 83,
  c: 67,
  t: 84,
  d: 68,
  e: 69,
  v: 86,
  zero: 48,
  keyZ: 90,
  plus: "Equal",
  numPlus: 107,
  minus: "Minus",
  numMinus: 109,
  period: 190,
  comma: 188,
  f11: 122,
  f1: 112,
};

export const Hotkey = {
  focusSearch: {
    label: "Search",
    keyCode: {
      ctrl: true,
      shift: true,
      keys: [KeyCode.f],
    },
  },
  toggleTree: {
    label: "Toogle Tree",
    keyCode: {
      ctrl: true,
      alt: true,
      keys: [KeyCode.t],
    },
  },
  help: {
    label: "Help",
    keyCode: {
      keys: [KeyCode.f1],
    },
  },

  readerLinkPrev: {
    label: "Previous Reader Link",
    keyCode: {
      ctrl: true,
      keys: [KeyCode.leftArrow],
    },
  },
  readerLinkNext: {
    label: "Next Reader Link",
    keyCode: {
      ctrl: true,
      keys: [KeyCode.rightArrow],
    },
  },

  openItem: {
    label: "Open element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.enter],
    },
  },
  expandItem: {
    label: "Expand element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: true,
      keys: [KeyCode.enter],
    },
  },
  checkItem: {
    label: "Check element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.space],
    },
  },
  itemContextMenu: {
    label: "Element context Menu",
    keyCode: {
      ctrl: true,
      alt: false,
      shift: false,
      keys: [KeyCode.enter],
    },
  },

  selectButtonOrLink: {
    label: "Select Button Or Link",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.enter, KeyCode.space],
    },
  },

  itemPrevX: {
    label: "Previous element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.leftArrow],
    },
  },
  itemNextX: {
    label: "Next element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.rightArrow],
    },
  },
  itemPrevY: {
    label: "Previous element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.upArrow],
    },
  },
  itemNextY: {
    label: "Next element",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.downArrow],
    },
  },

  exitFloatWindow: {
    label: "Exit popup",
    keyCode: {
      ctrl: false,
      alt: false,
      shift: false,
      keys: [KeyCode.esc],
    },
  },
};

const textInputs = ["INPUT", "TEXTAREA"];

export const isFocusTextInput = () => {
  if (document.activeElement) {
    return (
      document.activeElement.contentEditable === "true"
      || textInputs.includes(document.activeElement.tagName)
    );
  }
  return false;
};

export const isElementFocused = (element) => {
  if (document.activeElement) {
    return document.activeElement === element;
  }
  return false;
};

export const isEventKey = (e = {}, code) => {
  if (typeof code === "string") {
    return e.code === code;
  }

  return e.which === code || e.keyCode === code;
};

export const isEventKeys = (e = {}, codes = [], strict = false) => {
  return strict
    ? codes.every((code) => isEventKey(e, code))
    : codes.some((code) => isEventKey(e, code));
};

export const isHotKey = (event, hotkey) => {
  if (!event || !hotkey) {
    return false;
  }

  const hotkeyFinal = "keyCode" in hotkey ? hotkey.keyCode : hotkey;

  const isShift = !hotkeyFinal.shift || event.shiftKey;
  const isAlt = !hotkeyFinal.alt || event.altKey;
  const isCtrl = !hotkeyFinal.ctrl || (event.ctrlKey || event.metaKey);

  return (isShift && isAlt && isCtrl && isEventKeys(event, hotkeyFinal.keys));
};

export const getEventPath = (e) => {
  return e.path || (e.composedPath && e.composedPath()) || null;
};

export const makeFnGetFirstVisible = (
  scrollableView, isReverse, isHorizontal, checkFn, checkFnReverse
) => {
  if (!scrollableView) {
    return undefined;
  }
  const { children } = scrollableView;
  if (!children) {
    return undefined;
  }

  const keyOffsetElem = isHorizontal ? "offsetLeft" : "offsetTop";

  // client[Side] value for parent, to exclude border size value.
  const keySize = isHorizontal ? "clientWidth" : "clientHeight";
  // offset[Side] value for item, to include border size value.
  const keySizeElem = isHorizontal ? "offsetWidth" : "offsetHeight";

  const keyPaddingStart = isHorizontal ? "paddingLeft" : "paddingTop";
  const keyPaddingEnd = isHorizontal ? "paddingRight" : "paddingBottom";

  const computedStyle = window.getComputedStyle(scrollableView);
  const wrapStart = Math.round(isHorizontal ? scrollableView.scrollLeft : scrollableView.scrollTop)
    + parseFloat(computedStyle[keyPaddingStart]);
  const wrapEnd = wrapStart + scrollableView[keySize] + parseFloat(computedStyle[keyPaddingEnd]);

  let elem, elemStart;

  if (isReverse) {
    for (let i = children.length - 1; i >= 0; i--) {
      elem = children[i];
      elemStart = elem[keyOffsetElem];

      if (checkFnReverse(elemStart, wrapStart, elemStart + elem[keySizeElem], wrapEnd, elem)) {
        return elem;
      }
    }
  } else {
    for (let i = 0; i < children.length; i++) {
      elem = children[i];
      elemStart = elem[keyOffsetElem];

      if (checkFn(elemStart, wrapStart, elemStart + elem[keySizeElem], wrapEnd, elem)) {
        return elem;
      }
    }
  }


  return undefined;
};

export const getFirstVisible = (parent, isReverse, isHorizontal) => {
  return makeFnGetFirstVisible(parent, isReverse, isHorizontal,
    (elemStart, wrapStart, elemEnd) => {
      return isNumberBetweenEquals(wrapStart, elemStart, elemEnd);
    },
    (elemStart, wrapStart, elemEnd, wrapEnd) => {
      return isNumberBetweenEquals(wrapEnd, elemStart, elemEnd);
    }
  );
};

const OFFSET_AUTO_SCROLL_VIEW = 50;


export const contains = (rect, x, y) => {
  const { width, height, left, top } = rect;
  const newRect = { width, height, x: rect.x || left, y: rect.y || top };

  return (
    newRect.x <= x &&
    x <= newRect.x + newRect.width &&
    newRect.y <= y &&
    y <= newRect.y + newRect.height
  );
};

export const scrollViewIfEdge = (clientX, clientY) => {
  const scrollbarsViews = document.getElementsByClassName("scrollbars-render-view");
  let scrollbarsView, rect;
  for (let i = scrollbarsViews.length - 1; i >= 0; i--) {
    scrollbarsView = scrollbarsViews[i];
    rect = scrollbarsView.getBoundingClientRect();
    if (contains(rect, clientX, clientY)) {
      break;
    }
  }

  if (scrollbarsView) {
    const scrollViewY = rect.y;
    if (clientY - scrollViewY < OFFSET_AUTO_SCROLL_VIEW) {
      scrollbarsView.scroll(0, scrollbarsView.scrollTop - 1);
    } else if (clientY > rect.y + rect.height - OFFSET_AUTO_SCROLL_VIEW) {
      scrollbarsView.scroll(0, scrollbarsView.scrollTop + 1);
    }
  }
};

export const findClosestScrollView = (el) => {
  if (!el) {
    return null;
  }

  let node = null;
  do {
    node = (node || el).parentNode;
  } while (node && node instanceof Element && (
    // there is a case {scrollHeight} is greater by 1-2px than {offsetHeight}
    // e.g. search "god", open Related tab in S+,
    // find accordion "EGW Book Category by Topic",
    // check properties of ".accordion_body"
    // FIXME: Not in priority till is used in the single place, need to investigate.
    node.scrollHeight - node.offsetHeight <= 2
    || getComputedStyle(node).overflow === "hidden"
  ));
  return node === document ? window : node;
};

export const findClosestWithNonZeroPosition = (el) => {
  if (!el) {
    return null;
  }

  let computedStyle = null;

  let node = null;
  do {
    node = (node || el).parentNode;

    if (node && node instanceof Element) {
      computedStyle = getComputedStyle(node);
    } else {
      computedStyle = null;
    }
  } while (computedStyle && !(
    computedStyle.position !== "static"
    && (
      parseInt(computedStyle.top)
      || parseInt(computedStyle.left)
      || parseInt(computedStyle.bottom)
      || parseInt(computedStyle.right)
    )
  ));

  return computedStyle ? node : null;
};

// Stacking Context: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context#the_stacking_context
// Method can be expanded with the rest CSS-properties, that create a Stacking Context.
export const findClosestIndividualStackingContextContainer = (el) => {
  if (!el) {
    return null;
  }

  let node = null;

  do {
    node = (node || el).parentNode;
  } while (node && node instanceof Element && getComputedStyle(node).transform !== "none");

  return (node && node instanceof Element) ? node : null;
};

// If some parent of {el} has own Stacking Context and either the parent itself has a non-zero
// position or its child that is a parent to {el},
// then constantly VIEWPORT-related coordinates of {anchorEl.getBoundingClientRect()},
// should be re-calculated, to become regarding the parent with a non-zero offset parent.
export const getBoundaryRectConsideringStackingContext = (el) => {
  if (!el) {
    return null;
  }

  let rectEl = el.getBoundingClientRect();

  try {
    const elISC = findClosestIndividualStackingContextContainer(el);

    if (elISC) {
      const elWithNonZeroPosition = findClosestWithNonZeroPosition(el);

      if (elWithNonZeroPosition && elISC.contains(elWithNonZeroPosition)) {
        const computedStyleElStickyClosest = window.getComputedStyle(elWithNonZeroPosition);

        const top = parseInt(computedStyleElStickyClosest.top);
        const left = parseInt(computedStyleElStickyClosest.left);

        if (top || left) {
          rectEl = {
            top: rectEl.top - top,
            bottom: rectEl.bottom - top,
            left: rectEl.left - left,
            right: rectEl.right - left,

            width: rectEl.width,
            height: rectEl.height,
          };
        }
      }
    }
  } catch (e) {
    console.log(`%cgetBoundaryRectRegardingClosestStackingContext CRASHED, pls check!!!!`, "background: black; color: red; font-size: 16px;");
    console.log(e);
    console.groupCollapsed("Trace");
    console.trace();
    console.groupEnd();
  }

  return rectEl;
};

export const downloadFile = (uri, name) => {
  const fileName = name ? name : uri.substr(uri.lastIndexOf("/") + 1);
  let link = document.createElement("a");
  link.download = fileName;
  link.href = uri;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const dispatchCustomEvent = (type, detail, element = window) => {
  element.dispatchEvent(new CustomEvent(type, detail ? {
    detail,
  } : null));
};

export const getIsMatchesCssMediaHover = () => {
  return (
    global.window
    && global.window.matchMedia &&
    global.window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
};

/**
 * dont use very offen, long time calculation
 * @param {*} text
 * @param {*} classes
 */
export const calculateTextDimension = (text, classes = []) => {
  const dimensions = {
    width: 0,
    height: 0
  };

  if (!("window" in global)) {
    return dimensions;
  }

  classes.push("textDimensionCalculation");

  const div = document.createElement("div");
  div.setAttribute("class", classes.join(" "));
  div.innerHTML = text;
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.height = "auto";
  div.style.width = "auto";

  document.body.appendChild(div);

  dimensions.width = div.clientWidth;
  dimensions.height = div.clientHeight;
  removeElement(div);

  return dimensions;
};

export const inRect = (parentRect, rect, options) => {
  let fully, half, paddingVertical = 0;
  if (options) {
    fully = options.fully;
    half = options.half;
    paddingVertical = options.paddingVertical;
  }


  if (fully) {
    return (
      parentRect.left <= rect.left &&
      parentRect.right >= rect.right &&
      parentRect.top <= rect.top &&
      parentRect.bottom >= rect.bottom
    );
  }

  if (half) {
    return (
      parentRect.left < (rect.left + (rect.width / 2)) &&
      parentRect.right > (rect.right - (rect.width / 2)) &&
      parentRect.top < (rect.top + (rect.height / 2)) &&
      parentRect.bottom > (rect.bottom - (rect.height / 2))
    );
  }


  return (
    parentRect.left < rect.left &&
    parentRect.right > rect.left &&
    parentRect.top < (rect.bottom - paddingVertical) &&
    parentRect.bottom > (rect.top + paddingVertical)
  );
};

/**
 * Calculates the optimal position styles relate to the rect, that should not be covered
 * ("avoidRectangleRect") and boundaries that should not be crossed
 * ("boundaryRect").
 *
 * @param {Object} boundaryRect - The boundaries that should not be crossed.
 * @param {Object} avoidRectangleRect - The rect, that should not be covered.
 * @param {number} blockWidth - window width.
 * @param {number} blockHeight - window height.
 *
 * @returns {{top: number, left: number}}
 */
export const _deprecated_calculateFitPosition = (boundaryRect, avoidRectangleRect, blockWidth, blockHeight) => {
  let top, left;

  const topDelta = avoidRectangleRect.top - boundaryRect.top;
  const bottomDelta = boundaryRect.bottom - avoidRectangleRect.bottom;
  const leftDelta = avoidRectangleRect.right - boundaryRect.left;
  const rightDelta = boundaryRect.right - avoidRectangleRect.left;

  if (topDelta > bottomDelta) {
    top = avoidRectangleRect.top - blockHeight;
    top = clutch(top, 0, window.innerHeight);
  } else {
    top = avoidRectangleRect.bottom;
    top = clutch(top, 0, window.innerHeight - blockHeight);
  }

  if (leftDelta > rightDelta) {
    left = avoidRectangleRect.right - blockWidth;
    left = clutch(left, 0, window.innerWidth);
  } else {
    left = avoidRectangleRect.left;
    left = clutch(avoidRectangleRect.left, 0, window.innerWidth - blockWidth);
  }

  return { top, left };
};

/**
 * Calculates the optimal sizes styles relate to the rect, that should not be
 * covered ("avoidRectangleRect") and boundaries that should not be crossed
 * ("boundaryRect").
 *
 * @param {Object} boundaryRect - The boundaries that should not be crossed.
 * @param {Object} avoidRectangleRect - The rect, that should not be covered.
 *
 * @returns {{maxWidth: number, maxHeight: number}}
 */
export const _deprecated_calculateMaxSizes = (boundaryRect, avoidRectangleRect) => {
  let maxWidth, maxHeight;

  const topDelta = avoidRectangleRect.top - boundaryRect.top;
  const leftDelta = avoidRectangleRect.left - boundaryRect.left;
  const rightDelta = boundaryRect.right - avoidRectangleRect.right;
  const bottomDelta = boundaryRect.bottom - avoidRectangleRect.bottom;

  if (topDelta && bottomDelta) {
    if (topDelta > bottomDelta) {
      maxHeight = topDelta;
    } else {
      maxHeight = bottomDelta;
    }
  }

  if (leftDelta && rightDelta) {
    if (leftDelta > rightDelta) {
      maxWidth = leftDelta;
    } else {
      maxWidth = rightDelta;
    }
  }

  return { maxWidth, maxHeight };
};

/**
 * @description Returns touches array of event
 * @param {TouchEvent} e
 * @returns {Object}
 */
export const getTouches = (e) => {
  if (e.originalEvent) {
    if (e.originalEvent.touches && e.originalEvent.touches.length) {
      return e.originalEvent.touches;
    } else if (e.originalEvent.changedTouches && e.originalEvent.changedTouches.length) {
      return e.originalEvent.changedTouches;
    }
  }

  return e.changedTouches || e.touches;
};

export const getEventCoordinates = (e) => {
  let x = e.x === undefined ? e.clientX : e.x;
  let y = e.y === undefined ? e.clientY : e.y;

  if (x === undefined) {
    const touches = getTouches(e);

    if (touches && touches.length) {
      const lastTouch = touches[touches.length - 1];
      if (lastTouch) {
        x = lastTouch.clientX;
        y = lastTouch.clientY;
      }
    }
  }

  return {
    x,
    y
  };
};

// Value is initiated inside function {initiateValueIsBrowserSupportsFloatValueForScrollTop}.
export let isBrowserSupportsFloatValueForScrollTop;

(function initiateValueIsBrowserSupportsFloatValueForScrollTop () {
  if (!("window" in global)) {
    return undefined;
  }

  const elScrollableHeight = 50;

  const elScrollable = document.createElement("div");
  elScrollable.style.width = "50px";
  elScrollable.style.height = elScrollableHeight + "px";
  elScrollable.style.overflow = "scroll";

  const elContent = document.createElement("div");
  elContent.style.width = "50px";
  elContent.style.height = elScrollableHeight + 5 + "px";

  document.documentElement.appendChild(elScrollable);
  elScrollable.appendChild(elContent);

  elScrollable.scrollTop = 0.5;
  isBrowserSupportsFloatValueForScrollTop = elScrollable.scrollTop === 0.5;

  document.documentElement.removeChild(elScrollable);
  elScrollable.remove();
  elContent.remove();
})();

export const parseDOMRectToObject = (rect) => {
  if (!rect) {
    return rect;
  }

  const { top, right, bottom, left, width, height, x, y } = rect;
  return { top, right, bottom, left, width, height, x, y };
}

export const getBrowserScrollbarInfo = () => {
  let isScrollbarTakesContentPlace, scrollBarWidth;

  const width = 50;

  const elScrollable = document.createElement("div");
  elScrollable.style.height = "50px";
  elScrollable.style.width = width + "px";

  const elContent = document.createElement("div");
  elScrollable.appendChild(elContent);

  document.body.appendChild(elScrollable);

  elScrollable.style.overflow = "scroll";

  const widthScrollbarVisible = parseFloat(window.getComputedStyle(elContent).width);

  elScrollable.style.overflow = "hidden";

  const widthScrollbarHidden = parseFloat(window.getComputedStyle(elContent).width);

  const percentScrollbarHiddenVisible = widthScrollbarVisible * 100 / widthScrollbarHidden;
  if (percentScrollbarHiddenVisible > 0) {
    isScrollbarTakesContentPlace = true;
    scrollBarWidth = width - (width * (percentScrollbarHiddenVisible / 100));
  }

  document.body.removeChild(elScrollable);
  elScrollable.remove();
  elContent.remove();

  return {
    width: scrollBarWidth,
    isTakesContentPlace: isScrollbarTakesContentPlace,
  };
};

export const isBrowserSupportsSmoothScroll = ("window" in global && "CSS" in window && "supports" in window.CSS)
  ? window.CSS.supports('scroll-behavior', 'smooth')
  : null;


export const isFullScreenElementCrossBrowser = (element) => {
  if (element && element !== document.documentElement) {
    return !!(
      document.fullscreenElement === element ||
      document.mozFullScreenElement === element ||
      document.webkitFullscreenElement === element ||
      document.msFullscreenElement === element
    );
  }

  return !!(
    document.fullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement ||
    document.fullscreen ||
    document.mozFullScreen ||
    document.webkitIsFullScreen ||
    document.fullScreenMode
  );
}

export const getFnRequestFullScreenCrossBrowser = (element = document.documentElement) => {
  return (
    element.requestFullscreen
    || element.mozRequestFullScreen
    || element.webkitRequestFullscreen
    || element.msRequestFullscreen
  );
};

export const requestFullScreenCrossBrowser = (element = document.documentElement) => {
  const fn = getFnRequestFullScreenCrossBrowser(element);

  if (fn) {
    fn.call(element);
  }

  return null;
};

export const exitFullScreenCrossBrowser = () => {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  }
  if (document.mozCancelFullScreen) {
    return document.mozCancelFullScreen();
  }
  if (document.webkitExitFullscreen) {
    return document.webkitExitFullscreen();
  }
  if (document.msExitFullscreen) {
    return document.msExitFullscreen();
  }

  return null;
};

export const isEventDoubleClick = (event) => {
  return event.detail === 2;
};

export const scrollIntoViewIfVisibleLessThan = (element, coefficientVisibleLessThan) => {
  if (element) {
    const scrollView = findClosestScrollView(element);

    if (scrollView && scrollView !== window) {
      const scrollViewHeight = scrollView.offsetHeight;
      const scrollViewTop = scrollView.scrollTop;
      const scrollViewBottom = scrollViewTop + scrollViewHeight;

      const elHeight = element.offsetHeight;
      const elTop = element.offsetTop;
      const elBottom = elTop + elHeight;

      const deltaTop = scrollViewTop - elTop;
      const deltaBottom = elBottom - scrollViewBottom;

      const coefficientVisible = (
        scrollViewHeight + Math.min(0, deltaTop) + Math.min(0, deltaBottom)
      ) / scrollViewHeight;

      if ((deltaBottom > 0 || deltaTop > 0) && coefficientVisible < coefficientVisibleLessThan) {
        let topNew;

        if (deltaBottom > 0) {
          topNew = Math.min(scrollViewTop + deltaBottom, elTop);
        } else {
          topNew = Math.max(scrollViewTop - deltaTop, elBottom - scrollViewHeight);
        }

        scrollView.scroll({
          top: topNew,
          behavior: "smooth",
        });
      }
    }
  }
};

export const replaceElement = (el, newEl) => el.parentNode.replaceChild(newEl, el);
export const replaceAllElBySelector = (parent, selector, newEl) => {
  parent.querySelectorAll(selector).forEach((el) => replaceElement(el, newEl));
};

export const removeElement = (el) => el.parentNode.removeChild(el);
export const removeAllElBySelector = (parent, selector) => {
  parent.querySelectorAll(selector).forEach(removeElement);
};